import { Component, Inject, OnInit, OnDestroy } from '@angular/core';

import { MainFacade } from '../../../main.facade';
import { ProcessFacade } from '../process.facade';
import { CompressionOption } from '../../../models/request';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';
import { FormBuilder, Validators } from '@angular/forms';
import { RequestFactory } from '../../../models/request.factory';
import { TreeNode } from 'primeng/api';
import { IFileInfo } from '../../../models/file-info';
import { Subscription } from 'rxjs';
import {
  DEFAULT_OMICS_OPTIONS, OmicsOptions, OmicsPrepareService, OmicsPrepareStatus,
} from '../../../../services/omics-prepare.service';
import { SpatialDetection } from '../../../../services/spatial-omics.service';
import { VISUALIZER, IVisualizer } from '@jax-data-science/sci-image-visualizer';

export type ConvertMode = 'bioformats' | 'omics';

@Component({
  selector: 'convert',
  templateUrl: './convert.component.html',
  styleUrl: './convert.component.scss',
})
export class ConvertComponent extends  ProcessComponentBase implements OnInit, OnDestroy {

	// Convert uses the images selected
	// and constructs a valid Instruction
	// similar to slide cropper.
	public inputImages: IFileInfo[] = [];

	// Helper to get enum values as array
	public compressionOptions = Object.values(CompressionOption);

	/** Bioformats converts images with BFConvert; Omics prepares AnnData for Spatial Omics. */
	public mode: ConvertMode = 'bioformats';
	public readonly modes = [
	  { label: 'Bioformats', value: 'bioformats' },
	  { label: 'Omics', value: 'omics' },
	];
	/** The selected file or folder, the omics dataset to prepare. */
	public omicsTarget: IFileInfo | null = null;
	/** What the server says the target is; null while unknown or being detected. */
	public omicsDetection: SpatialDetection | null = null;
	public omicsDetecting = false;
	public omicsStatus: OmicsPrepareStatus | null = null;
	/** What the job builds besides the dataset, for one that can have a transcript pyramid. */
	public omicsOptions: OmicsOptions = { ...DEFAULT_OMICS_OPTIONS };
	public omicsError: string | null = null;
	private omicsWatch?: Subscription;
	private omicsDetectSeq = 0;

	isOutputNameEditable = false;
	isOutputFolderEditable = false;

	private levelSizeSubscription!: Subscription;

	public levels = [{ shape: '0: 512x512', level: 0 }];


	private static describe(err: unknown): string {
	  const e = err as { error?: { error?: string; message?: string }; message?: string };
	  return e?.error?.error ?? e?.error?.message ?? e?.message ?? String(err);
	}

	constructor(
	  mainFacade: MainFacade,
	  @Inject(VISUALIZER) private plotService: IVisualizer,
	  private processFacade: ProcessFacade,
	  private fb: FormBuilder		,
		submissionState: SubmissionStateService,
	  private omicsService: OmicsPrepareService) {

	  super('Convert', submissionState, mainFacade);
	}

	ngOnInit(): void {
	  this.configForm();
		this.mainFacade.getSelectedNode$().subscribe({
		  next: (node) => {
		    if (node == undefined || node.data == undefined) {
		      this.bucket = undefined;
		      this.setOmicsTarget(null);
		    } else {
		      this.setImagesInput(node);
		      this.setOmicsTarget(node.data);
		    }
		  },
		  error: (err) => {
		    console.error(`Error with node selection: ${err}`);
		  },
		});
		this.mainFacade.getValidationMessage$().subscribe({
		  next: (message) => {
		    this.validationMessage = message;
		  }
		});
		this.levelSizeSubscription = this.plotService.getImageMeta().subscribe(imageMeta => {
		  this.levels = [];
		  for (let i = 0; i < imageMeta.length; i++) {
		    this.levels.push({ shape: `${ i }: ${ imageMeta[i].x }x${ imageMeta[i].y }`, level: i });
		  }

		  const levelDisabled = this.levels.length !== 1;
		  if (levelDisabled) {
		    this.form.get('level')?.enable();
		  } else {
		    this.form.get('level')?.disable();
		  }
		});

  }

	setImagesInput(node: TreeNode<IFileInfo>) {
	  this.inputImages = [];
	  // if folder
	  if (node && node.children && node.children.length > 0) {
	    node.children?.forEach(child => {
	      if (child.data) {
	        this.inputImages.push(child.data);
	      }
	    });
	  } else if (node && node.children && node.children.length == 0) {
	    // if file
	    if (node.data) {
	      this.inputImages.push(node.data);
	    }
	  }
	  this.bucket = node.data?.project.bucket;
	  if (node.leaf) {
	    this.path = node.parent?.data?.relPath;
	  } else {
	    this.path = node.data?.relPath;
	  }

	}

	// ── omics mode ──

	setMode(mode: ConvertMode) {
	  this.mode = mode;
	  if (mode === 'omics') this.detectOmicsTarget();
	}

	/**
	 * True when Run has something to do: an omics dataset that is not under way and is either not
	 * ready or lacks a part the options ask for.
	 */
	get canPrepareOmics(): boolean {
	  const s = this.omicsStatus?.state;
	  if (!this.omicsDetection?.id || !OmicsPrepareService.isPreparable(this.omicsDetection.kind)) return false;
	  if (s === 'submitting' || s === 'running') return false;
	  return s !== 'ready' || OmicsPrepareService.missing(this.omicsStatus?.built, this.validOmicsOptions);
	}

	/**
	 * The options as they are submitted: levels a whole number from 1 to 8, the default (7) when the
	 * field is empty or not a number, since clearing a number input sets it to null.
	 */
	get validOmicsOptions(): OmicsOptions {
	  const levels = Number(this.omicsOptions.levels);
	  const valid = Number.isFinite(levels) && this.omicsOptions.levels !== null
	    ? Math.min(8, Math.max(1, Math.round(levels))) : DEFAULT_OMICS_OPTIONS.levels;
	  return { ...this.omicsOptions, levels: valid };
	}

	/** Leaving the levels field empty or out of range puts back the value that will be used. */
	onOmicsLevelsBlur() {
	  this.omicsOptions.levels = this.validOmicsOptions.levels;
	}

	/** Whether the target can have optional parts (a transcript pyramid; a zip's morphology). */
	get hasOmicsOptions(): boolean {
	  return !!this.omicsStatus?.built;
	}

	/** Whether the target can have prepared marker genes (a Xenium bundle with clusterings). */
	get hasMarkersOption(): boolean {
	  return this.omicsStatus?.built?.markers !== undefined;
	}

	/** Whether the target can have extracted morphology images (a zipped Xenium bundle). */
	get hasMorphologyOption(): boolean {
	  return this.omicsStatus?.built?.morphology !== undefined;
	}

	omicsKindLabel(kind: string | undefined): string {
	  switch (kind) {
	    case 'h5ad': return 'AnnData (.h5ad)';
	    case 'spatialdata': return 'SpatialData store';
	    case 'xenium-zip': return '10x Xenium bundle (zip)';
	    case 'xenium-folder': return '10x Xenium bundle (folder)';
	    default: return kind ?? '';
	  }
	}

	canPrepareKind(kind: string | undefined): boolean {
	  return OmicsPrepareService.isPreparable(kind);
	}

	get omicsBusy(): boolean {
	  return this.omicsStatus?.state === 'submitting' || this.omicsStatus?.state === 'running';
	}

	private setOmicsTarget(target: IFileInfo | null) {
	  this.omicsTarget = target;
	  this.omicsWatch?.unsubscribe();
	  this.omicsStatus = null;
	  this.omicsError = null;
	  this.omicsDetection = null;
	  this.omicsOptions = { ...DEFAULT_OMICS_OPTIONS };
	  if (this.mode === 'omics') this.detectOmicsTarget();
	}

	private async detectOmicsTarget() {
	  const target = this.omicsTarget;
	  if (!target || this.omicsDetection) return;
	  const seq = ++this.omicsDetectSeq;
	  this.omicsDetecting = true;
	  try {
	    const detection = await this.omicsService.detect(target);
	    if (seq !== this.omicsDetectSeq) return; // the selection moved on
	    this.omicsDetection = detection;
	    if (detection.id && OmicsPrepareService.isPreparable(detection.kind)) {
	      this.omicsService.status(detection.id).subscribe({
	        next: (status) => {
	          if (seq !== this.omicsDetectSeq) return;
	          this.omicsStatus = status;
	          if (status.state === 'submitting' || status.state === 'running') this.watchOmics(detection.id as string);
	        },
	        error: (err) => this.omicsError = ConvertComponent.describe(err),
	      });
	    }
	  } catch (err) {
	    if (seq === this.omicsDetectSeq) this.omicsError = ConvertComponent.describe(err);
	  } finally {
	    if (seq === this.omicsDetectSeq) this.omicsDetecting = false;
	  }
	}

	/** Submit the omics convert job for the target and follow it to the end. */
	onPrepareOmics() {
	  const id = this.omicsDetection?.id;
	  if (!id || !this.canPrepareOmics) return;
	  this.omicsError = null;
	  // Options only mean something for a dataset that can have a transcript pyramid.
	  this.omicsService.prepare(id, this.hasOmicsOptions ? this.validOmicsOptions : undefined).subscribe({
	    next: (status) => {
	      this.omicsStatus = status;
	      if (status.state !== 'ready' && status.state !== 'failed') this.watchOmics(id);
	    },
	    error: (err) => this.omicsError = ConvertComponent.describe(err),
	  });
	}

	private watchOmics(id: string) {
	  this.omicsWatch?.unsubscribe();
	  this.omicsWatch = this.omicsService.watch(id).subscribe({
	    next: (status) => this.omicsStatus = status,
	    error: (err) => this.omicsError = ConvertComponent.describe(err),
	  });
	}

	ngOnDestroy(): void {
	  this.omicsWatch?.unsubscribe();
		if (this.levelSizeSubscription) {
		  this.levelSizeSubscription.unsubscribe();
		}
	};

	configForm() {
	  this.form = this.fb.group({
			level: [0],
			compression: [null, Validators.required],
			channel: [null, Validators.required],
		  z: [null, Validators.required],
			timepoint: [null, Validators.required],
			tilex: [null, Validators.required],
			tiley: [null, Validators.required],
			outputName: [null, Validators.required],
			outputFolder: [null, Validators.required],
			editOutputName: [false],
		  editOutputFolder: [false],
	  });
	  this.persistForm();
	}

	onSubmit() {
	  super.submit(this.buildRequest());
	}

	onCreateRule() {
	  super.createRule(this.buildRequest());
	}

	private buildRequest() {
	  const formValue = this.form.value;
	  const bucket = this.bucket;
	  const parentPath = this.path;
	  const convertRequest = RequestFactory.getConvertRequest(formValue, bucket, parentPath);

		convertRequest.manualRegions = this.plotService.getRegionPolygons();

	  return convertRequest;
	}


	onEditOutputName(val: boolean) {
	  this.isOutputNameEditable = val;
	}

	onEditOutputFolder(val: boolean) {
	  this.isOutputFolderEditable = val;
	}

}
